import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { PageShell } from '@/components/layout/PageShell';
import { SectionMarker } from '@/components/brand/SectionMarker';
import { Icon } from '@/components/ui/Icon';
import { NeonButton } from '@/components/ui/NeonButton';
import { ROUTES } from '@/config/routes';
import { useConversations } from '@/modules/messages/useConversations';
import { useMessages, type HottrMessage } from '@/modules/messages/useMessages';
import { capture } from '@/modules/heat/signals';
import { cn } from '@/lib/cn';

export const MessagesPage = () => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  const { conversations, loading: convsLoading } = useConversations();
  const { messages, sending, sendMessage } = useMessages(activeId);

  useEffect(() => { capture('surface_enter', 'messages'); }, []);

  useEffect(() => {
    if (!activeId && conversations.length > 0) {
      setActiveId(conversations[0].id);
    }
  }, [conversations, activeId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const active = conversations.find((c) => c.id === activeId) ?? null;

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    const draft = input;
    setInput('');
    await sendMessage(draft);
  };

  return (
    <PageShell
      rail={false}
      mobileTopBar={
        <>
          {/* Identity is the WorldSwitcher in PageShell's bar — one lockup.
              This spacer keeps the compose button on the right edge. */}
          <span aria-hidden />
          <button
            aria-label="New message"
            className="text-text-secondary transition-colors duration-fast ease-signal hover:text-pink"
          >
            <Icon name="edit_square" size={20} />
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 lg:h-[calc(100dvh-4rem)] lg:grid-cols-[420px_minmax(0,1fr)]">
        {/* THREAD LIST */}
        <section
          className={cn(
            'border-white/[0.06] bg-ink',
            'lg:overflow-y-auto lg:border-r',
            'px-5 py-7 md:px-10 md:py-10 lg:px-7 lg:py-8',
          )}
        >
          <header className="mb-7">
            <SectionMarker className="mb-3">Inbox · who&apos;s still up</SectionMarker>
            <h1 className="font-display text-3xl font-black uppercase leading-none tracking-display md:text-4xl">
              Say less<span className="text-pink">.</span>{' '}
              <span className="font-light italic text-text-secondary">Mean more</span>
              <span className="text-pink">.</span>
            </h1>
          </header>

          {convsLoading ? (
            <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
              Loading<span className="text-pink">.</span>
            </p>
          ) : conversations.length === 0 ? (
            <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
              No messages yet<span className="text-pink">.</span> Find someone on the feed and say something.
            </p>
          ) : (
            <ul className="divide-y divide-white/[0.06]">
              {conversations.map((conv) => {
                const isActive = conv.id === activeId;
                return (
                  <li key={conv.id}>
                    <button
                      type="button"
                      onClick={() => setActiveId(conv.id)}
                      className={cn(
                        'group flex w-full items-center gap-4 py-3 text-left',
                        'transition-colors duration-fast ease-signal',
                        isActive && 'lg:bg-white/[0.03]',
                      )}
                    >
                      <div className="relative h-14 w-14 shrink-0 overflow-hidden border border-white/[0.07]">
                        {conv.otherPhotoUrl ? (
                          <img src={conv.otherPhotoUrl} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <div className="h-full w-full bg-charcoal" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <p className="truncate font-display text-sm font-bold uppercase tracking-wide text-text-primary group-hover:text-pink">
                            @{conv.otherHandle}
                          </p>
                          {conv.lastMessageAt && (
                            <span className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
                              {formatAgo(conv.lastMessageAt)}
                            </span>
                          )}
                        </div>
                        <p className={cn('mt-1 truncate text-xs', conv.unreadCount > 0 ? 'text-text-primary' : 'text-text-muted')}>
                          {conv.lastMessage ?? 'No messages yet'}
                        </p>
                      </div>
                      {conv.unreadCount > 0 && (
                        <span aria-label={`${conv.unreadCount} unread`} className="h-1.5 w-1.5 shrink-0 rounded-full bg-pink shadow-glow-soft" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <p className="mt-10 text-center font-display text-[9px] font-bold uppercase tracking-widest text-text-muted">
            Messages live inside HOTTR<span className="text-pink">.</span>app<span className="text-pink">.</span>{' '}
            No separate chat brand.
          </p>
        </section>

        {/* CONVERSATION PANE — desktop only */}
        <section className="relative hidden bg-ink lg:flex lg:flex-col">
          {active ? (
            <>
              <div className="flex items-center gap-4 border-b border-white/[0.07] px-9 py-5">
                <div className="h-10 w-10 shrink-0 overflow-hidden border border-white/[0.07]">
                  {active.otherPhotoUrl ? (
                    <img src={active.otherPhotoUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full bg-charcoal" />
                  )}
                </div>
                <p className="font-display text-base font-black uppercase tracking-tight">
                  @{active.otherHandle}<span className="text-pink">.</span>
                </p>
                <Link
                  to={ROUTES.profile(active.otherHandle)}
                  className="ml-auto font-display text-[10px] font-bold uppercase tracking-widest text-text-muted hover:text-pink"
                >
                  Profile →
                </Link>
              </div>

              <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-9 py-8">
                {messages.length === 0 && (
                  <p className="self-center font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
                    Say something<span className="text-pink">.</span>
                  </p>
                )}
                {messages.map((m) => (
                  <MessageBubble key={m.id} message={m} otherUserId={active.otherUserId} />
                ))}
                <div ref={bottomRef} />
              </div>

              <form onSubmit={handleSend} className="flex items-center gap-3 border-t border-white/[0.07] bg-ink px-9 py-5">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  disabled={sending}
                  className={cn(
                    'flex-1 border border-white/[0.10] bg-charcoal/40 px-4 py-3 text-sm',
                    'placeholder:text-text-muted focus:border-pink focus:outline-none disabled:opacity-50',
                  )}
                  placeholder="say less. mean more."
                />
                <NeonButton type="submit" size="md" disabled={sending || !input.trim()} trailing={<Icon name="arrow_forward" size={14} />}>
                  {sending ? '…' : 'Send'}
                </NeonButton>
              </form>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center px-12 text-center">
              <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
                Pick a thread<span className="text-pink">.</span>
              </p>
            </div>
          )}
        </section>
      </div>
    </PageShell>
  );
};

const MessageBubble = ({ message, otherUserId }: { message: HottrMessage; otherUserId: string }) => {
  const isMe = message.senderId !== otherUserId;
  return (
    <div className={cn('flex max-w-[60%] flex-col gap-1', isMe ? 'self-end items-end' : 'self-start items-start')}>
      <p className={cn('px-4 py-2.5 text-sm leading-snug', isMe ? 'bg-pink text-night' : 'border border-white/[0.07] bg-charcoal/60 text-text-primary')}>
        {message.content}
      </p>
      {isMe && message.readAt && (
        <span className="inline-flex items-center gap-1 font-display text-[9px] font-bold uppercase tracking-widest text-pink">
          <Icon name="done_all" size={11} />
          Read
        </span>
      )}
    </div>
  );
};

const formatAgo = (iso: string): string => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
};
